import { t } from "@hakili/i18n";
import { useEffect, useState } from "react";

/**
 * `round CN-C`, `CN-C.3`, WF-CON-41: **« Supervision de l'agent », to the
 * drawing (`hakili-console.jsx`, `SupervisionScreen`), the administration
 * alone.**
 *
 * The four indicators, « Issue du filtre », « Où l'agent bute » and the
 * exceptions come from `GET /intelligence/supervision`: LIVE where
 * intelligence answers (the dossiers read this month, the share without a
 * human review, the refusals awaiting a human and their causes) and a DASH
 * where it does not (the delay, the filter's outcome , lending's fact).
 * The roadmap sentence reads the FIRST cause's count from the same array the
 * list is drawn from , one number, never a second source. « Traiter » is
 * greyed with its round: the door does not exist.
 */
export interface SupervisionView {
  readonly month: string;
  readonly dossiersRead: number;
  readonly handledWithoutHumanPct: number | null;
  readonly exceptionsPending: number;
  readonly averageDelayHours: number | null;
  readonly filterOutcome: {
    readonly qualifiedAndSubmitted: number | null;
    readonly returnedForCorrection: number | null;
    readonly refusedAtFilter: number | null;
  };
  readonly causes: readonly { readonly reason: string; readonly count: number }[];
  readonly exceptions: readonly {
    readonly id: string;
    readonly dossierReference: string | null;
    readonly clientName: string | null;
    readonly reason: string;
    readonly refusedAt: string;
  }[];
}

export interface SupervisionDoors {
  readonly readSupervisionOfAgent: () => Promise<SupervisionView>;
  /** `K-1`, `M4`: the closures' coded motives, counted (`GET /dossiers/closures/reasons`). */
  readonly readClosureReasons?: () => Promise<readonly ClosureReasonView[]>;
}

export interface ClosureReasonView {
  readonly code: string;
  readonly label: string;
  readonly count: number;
}

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

const figure = (value: number | null, suffix = ""): string =>
  value === null ? DASH : `${String(value)}${suffix}`;

export function Supervision(props: {
  readonly view: SupervisionView | null;
  readonly refusal: string | null;
  /** `K-1`, `M4`: « Motifs de clôture sans suite », one count per motive; `undefined` where the door is not wired. */
  readonly closureReasons?: readonly ClosureReasonView[] | null;
}): React.JSX.Element {
  const { view } = props;
  const first = view?.causes[0] ?? null;
  const kpis = [
    {
      id: "filtres",
      label: t("console.supervision.kpiFiltres"),
      value: view === null ? DASH : String(view.dossiersRead),
      sub: t("console.supervision.kpiFiltresSous"),
      highlight: false,
    },
    {
      id: "sans-intervention",
      label: t("console.supervision.kpiSansIntervention"),
      value: view === null ? DASH : figure(view.handledWithoutHumanPct, " %"),
      sub: t("console.supervision.kpiSansInterventionSous"),
      highlight: true,
    },
    {
      id: "exceptions",
      label: t("console.supervision.kpiExceptions"),
      value: view === null ? DASH : String(view.exceptionsPending),
      sub: t("console.supervision.kpiExceptionsSous"),
      highlight: false,
    },
    {
      id: "delai",
      label: t("console.supervision.kpiDelai"),
      value: view === null ? DASH : figure(view.averageDelayHours, " h"),
      sub: t("console.supervision.kpiDelaiSous"),
      highlight: false,
    },
  ];
  const outcome = [
    {
      label: t("console.supervision.issueQualifies"),
      value: view?.filterOutcome.qualifiedAndSubmitted ?? null,
      color: "bg-emerald-400",
    },
    {
      label: t("console.supervision.issueRenvoyes"),
      value: view?.filterOutcome.returnedForCorrection ?? null,
      color: "bg-amber-400",
    },
    {
      label: t("console.supervision.issueRefuses"),
      value: view?.filterOutcome.refusedAtFilter ?? null,
      color: "bg-rose-400",
    },
  ];
  return (
    <div data-testid="supervision">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t("console.supervision.titre")}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{t("console.supervision.sousTitre")}</p>
      </div>
      {props.refusal === null ? null : (
        <p role="alert" data-testid="supervision-refus" className="mb-3 text-sm text-rose-600">
          {props.refusal}
        </p>
      )}
      <div className="mb-6 grid grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.id}
            data-testid={`supervision-kpi-${kpi.id}`}
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
          >
            <p className="text-xs text-gray-500">{kpi.label}</p>
            <p
              className={
                "mt-1 text-2xl font-semibold tabular-nums " + (kpi.highlight ? "text-indigo-600" : "")
              }
            >
              {kpi.value}
            </p>
            <p className="mt-1 text-xs text-gray-500">{kpi.sub}</p>
          </div>
        ))}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
          <h2 className="mb-1 text-sm font-semibold">{t("console.supervision.issueTitre")}</h2>
          <ul data-testid="supervision-issue" className="mt-2 space-y-2">
            {outcome.map((one) => (
              <li key={one.label} className="flex items-center gap-3">
                <span className={"h-2 w-2 shrink-0 rounded-full " + one.color} />
                <span className="flex-1 text-sm text-gray-700">{one.label}</span>
                <span className="text-sm font-medium tabular-nums">{figure(one.value)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-gray-500">{t("console.supervision.issueRond")}</p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
          <h2 className="mb-1 text-sm font-semibold">{t("console.supervision.buteTitre")}</h2>
          {view === null ? (
            <p className="text-sm text-gray-500">{t("common.state.chargement")}</p>
          ) : view.causes.length === 0 ? (
            <p data-testid="supervision-bute-vide" className="text-sm text-gray-500">
              {t("console.supervision.buteVide")}
            </p>
          ) : (
            <ul data-testid="supervision-causes" className="mt-2 space-y-2">
              {view.causes.map((cause) => (
                <li key={cause.reason} className="flex items-center gap-3">
                  <span className="flex-1 text-sm text-gray-700">{cause.reason}</span>
                  <span className="text-sm font-medium tabular-nums">{cause.count}</span>
                </li>
              ))}
            </ul>
          )}
          {first === null ? null : (
            // The sentence reads the same array as the line above it: one number, by construction.
            <p
              data-testid="supervision-feuille-de-route"
              className="mt-2 text-xs leading-relaxed text-gray-500"
            >
              {fill("console.supervision.feuilleDeRoute", {
                n: String(first.count),
                cause: first.reason.toLowerCase(),
              })}
            </p>
          )}
        </div>
      </div>

      <h2 className="mb-3 text-sm font-semibold">{t("console.supervision.exceptionsTitre")}</h2>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {view === null ? (
          <p className="px-5 py-5 text-sm text-gray-500">{t("common.state.chargement")}</p>
        ) : view.exceptions.length === 0 ? (
          <p data-testid="supervision-exceptions-vide" className="px-5 py-5 text-sm text-gray-500">
            {t("console.supervision.exceptionsVide")}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {view.exceptions.map((one) => (
              <li
                key={one.id}
                data-testid={`exception-${one.id}`}
                className="flex items-center gap-4 px-5 py-3"
              >
                <div className="w-48 shrink-0">
                  <p className="text-sm font-medium text-gray-900">{one.dossierReference ?? DASH}</p>
                  <p className="text-xs text-gray-500">{one.clientName ?? DASH}</p>
                </div>
                <p className="flex-1 text-sm text-gray-700">{one.reason}</p>
                <span className="w-24 text-xs text-gray-500">
                  {t("console.supervision.gravite")} {DASH}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* `K-1`, `M4`: the chargés' coded motives, counted , WF-CON-19 amended, CN-C.3's screen. */}
      {props.closureReasons === undefined ? null : (
        <>
          <h2 className="mt-6 mb-1 text-sm font-semibold">{t("console.supervision.motifsTitre")}</h2>
          <p className="mb-3 text-sm text-gray-500">{t("console.supervision.motifsSous")}</p>
          <div
            data-testid="supervision-motifs"
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
          >
            {props.closureReasons === null ? (
              <p className="px-5 py-5 text-sm text-gray-500">{t("common.state.chargement")}</p>
            ) : props.closureReasons.every((one) => one.count === 0) ? (
              <p data-testid="supervision-motifs-vide" className="px-5 py-5 text-sm text-gray-500">
                {t("console.supervision.motifsVide")}
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {props.closureReasons.map((one) => (
                  <li
                    key={one.code}
                    data-testid={`motif-${one.code}`}
                    className="flex items-center justify-between gap-4 px-5 py-3"
                  >
                    <p className="text-sm text-gray-700">{one.label}</p>
                    <span data-testid="motif-compte" className="text-sm tabular-nums text-gray-900">
                      {String(one.count)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function SupervisionScreen(props: SupervisionDoors): React.JSX.Element {
  const [view, setView] = useState<SupervisionView | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [closureReasons, setClosureReasons] = useState<readonly ClosureReasonView[] | null>(null);
  const { readSupervisionOfAgent, readClosureReasons } = props;
  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        // `K-1`, `M4`: its own part, read FIRST and on its own , the agent's supervision may
        // refuse (its door is another round's) and the count still stands.
        if (readClosureReasons !== undefined) {
          const reasons = await readClosureReasons().catch(() => [] as readonly ClosureReasonView[]);
          if (run.live) setClosureReasons(reasons);
        }
        const answered = await readSupervisionOfAgent();
        if (run.live) setView(answered);
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readSupervisionOfAgent, readClosureReasons]);
  return (
    <Supervision
      view={view}
      refusal={refusal}
      {...(readClosureReasons === undefined ? {} : { closureReasons })}
    />
  );
}
